import { useEffect, type ReactNode } from "react";
import { Command } from "cmdk";
import { ArrowRight, Search, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy } from "../lib/microcopy";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./overlays";

// ---------------------------------------------------------------- shortcut hint
/** Keys in the order they are pressed, read in the page's direction: "Ctrl" then "K" from the right in Arabic as from the left in English. */
export function Shortcut({ keys }: { keys: string[] }) {
  return (
    <span className="flex items-center gap-1">
      {keys.map((k, i) => (
        <kbd key={i} className="rounded border border-sand-200 bg-sand-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-sand-600">
          {k}
        </kbd>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------- command palette (generic)
export interface PaletteItem {
  id: string;
  label: ReactNode;
  /** Extra words the fuzzy filter should match on (keys, Arabic names, paths). */
  keywords?: string;
  hint?: ReactNode;
  icon?: LucideIcon;
  /** Rendered instead of the icon (e.g. a kind badge). */
  leading?: ReactNode;
  /** Keyboard sequence shown at the end (e.g. `["g", "s"]`). */
  keys?: string[];
  trailing?: ReactNode;
  onSelect: () => void;
}

export interface PaletteGroup {
  id: string;
  heading: ReactNode;
  items: PaletteItem[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Groups are rendered in order; empty groups are skipped. */
  groups: PaletteGroup[];
  query: string;
  onQueryChange: (q: string) => void;
  /** Accessible label of the palette. */
  label: string;
  placeholder: string;
  emptyText: string;
  /** Let cmdk filter items by their text; turn off when the groups already reflect a server-side search. */
  shouldFilter?: boolean;
}

/**
 * Prop-driven command palette on `cmdk`. Apps supply the groups (navigation, actions, recent, search hits) and own routing and
 * data fetching; the palette owns the dialog, the input, keyboard handling and the look.
 */
export function CommandPalette({ open, onOpenChange, groups, query, onQueryChange, label, placeholder, emptyText, shouldFilter = true }: CommandPaletteProps) {
  useEffect(() => {
    if (open) onQueryChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (item: PaletteItem) => {
    onOpenChange(false);
    item.onSelect();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="overflow-hidden p-0" aria-describedby={undefined} hideClose>
        <Command label={label} className="flex max-h-[70dvh] flex-col" shouldFilter={shouldFilter} loop>
          <div className="flex items-center gap-2 border-b border-sand-200 px-4 has-[input:focus-visible]:shadow-[inset_0_-2px_0_var(--color-ring)]">
            <Search className="size-4 shrink-0 text-sand-500" />
            <Command.Input value={query} onValueChange={onQueryChange} placeholder={placeholder} className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-sand-500 focus-visible:ring-0 focus-visible:ring-offset-0" autoFocus />
            <DialogClose aria-label={microcopy("close")} className="shrink-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <kbd className="block rounded border border-sand-200 bg-sand-50 px-1.5 py-0.5 text-[10px] font-medium text-sand-600 hover:bg-sand-100">esc</kbd>
            </DialogClose>
          </div>
          <Command.List className="scrollbar-thin overflow-y-auto p-2">
            <Command.Empty className="px-3 py-8 text-center text-sm text-muted-foreground">{emptyText}</Command.Empty>
            {groups
              .filter((g) => g.items.length > 0)
              .map((g) => (
                <Command.Group key={g.id} heading={g.heading} className={groupClass}>
                  {g.items.map((item) => (
                    <Command.Item key={item.id} value={`${g.id} ${item.id} ${typeof item.label === "string" ? item.label : ""} ${item.keywords ?? ""}`} onSelect={() => pick(item)} className={itemClass}>
                      {item.leading ?? (item.icon ? <item.icon className="size-4 text-aegold-700" /> : <ArrowRight className="size-4 text-sand-500" />)}
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.hint ? <span className="truncate text-xs text-muted-foreground">{item.hint}</span> : null}
                      {item.trailing}
                      {item.keys ? <Shortcut keys={item.keys} /> : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

const groupClass = "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-sand-600";
const itemClass = cn(
  "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors",
  "data-[selected=true]:bg-aegold-50 data-[selected=true]:text-aegold-900 aria-selected:bg-aegold-50",
);

// ---------------------------------------------------------------- shortcuts dialog
export interface ShortcutRow {
  keys: string[];
  label: ReactNode;
}

export function ShortcutsDialog({ open, onOpenChange, title, description, rows }: { open: boolean; onOpenChange: (o: boolean) => void; title: ReactNode; description?: ReactNode; rows: ShortcutRow[] }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <ul className="divide-y divide-sand-100 text-sm">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between gap-4 py-2">
              <span>{r.label}</span>
              <Shortcut keys={r.keys} />
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
